// Org admin: configure Microsoft 365 / Azure AD app at the organization level.
// Supports both Delegated (per-user OAuth) and Application Permissions
// (client-credentials, no per-user consent — see examples/interface_roadscorrespondences_azure.py).
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAppStore } from '@/store/app';
import apiClient from '@/api';

interface MS365Config {
  enabled: boolean;
  authType: 'delegated' | 'application_permissions';
  tenantId: string | null;
  clientId: string | null;
  hasClientSecret: boolean;
  allowedDomains: string[];
  adminConsentGranted: boolean;
  accessibleMailboxes: Array<{ email: string; displayName?: string; isActive?: boolean }>;
  validationStatus: 'not_configured' | 'pending' | 'valid' | 'invalid';
  validationError?: string | null;
}

const blank = (): MS365Config => ({
  enabled: false,
  authType: 'delegated',
  tenantId: null,
  clientId: null,
  hasClientSecret: false,
  allowedDomains: [],
  adminConsentGranted: false,
  accessibleMailboxes: [],
  validationStatus: 'not_configured'
});

export const Microsoft365Settings: React.FC<{ orgId: string }> = ({ orgId }) => {
  const { addNotification } = useAppStore();
  const [config, setConfig] = useState<MS365Config>(blank());
  const [clientSecretInput, setClientSecretInput] = useState('');
  const [allowedDomainsInput, setAllowedDomainsInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      // apiClient.get auto-unwraps {success:true, data:{...}} -> {...}, so the
      // response shape we see here is the inner object directly: { microsoft365,
      // googleWorkspace, imapDefaults, allowedProviders }.
      const res: any = await apiClient.orgSettings.getMicrosoft365(orgId);
      const ms365 = res?.microsoft365 || res?.data?.microsoft365 || {};
      setConfig({ ...blank(), ...ms365 });
      setAllowedDomainsInput((ms365.allowedDomains || []).join(', '));
      setClientSecretInput('');
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Load failed', message: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  }, [orgId, addNotification]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const allowedDomains = allowedDomainsInput
        .split(',')
        .map(d => d.trim().toLowerCase())
        .filter(Boolean);

      const payload: any = {
        enabled: config.enabled,
        authType: config.authType,
        tenantId: config.tenantId || undefined,
        clientId: config.clientId || undefined,
        allowedDomains
      };
      if (clientSecretInput.trim()) {
        payload.clientSecret = clientSecretInput.trim();
      }

      await apiClient.orgSettings.updateMicrosoft365(orgId, payload);
      addNotification?.({ type: 'success', title: 'Microsoft 365 saved', message: 'Configuration saved. Validate to test the connection.' });
      setClientSecretInput('');
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Save failed', message: err?.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      // The validate endpoint returns { success, message, error, details } at the
      // top level (no `data` wrapper), so the auto-unwrap doesn't apply here.
      const res: any = await apiClient.orgSettings.validateMicrosoft365(orgId);
      const success = res?.success !== false; // treat truthy or missing as success
      if (success) {
        addNotification?.({ type: 'success', title: 'Validated', message: res?.message || 'Microsoft 365 configuration looks good.' });
      } else {
        addNotification?.({ type: 'error', title: 'Validation failed', message: res?.error || 'Could not validate.' });
      }
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Validation error', message: err?.response?.data?.error || err.message });
    } finally {
      setValidating(false);
    }
  };

  const adminConsentUrl = (): string | null => {
    if (!config.tenantId || !config.clientId) return null;
    // Per-tenant admin consent endpoint. After consent, Microsoft redirects to
    // the redirect_uri configured on the app — we point them back to this
    // settings page so the user can re-validate.
    const redirectUri = encodeURIComponent(`${window.location.origin}/org-settings?ms365_consent=success`);
    return `https://login.microsoftonline.com/${config.tenantId}/adminconsent?client_id=${config.clientId}&redirect_uri=${redirectUri}`;
  };

  // After admin consent redirects back, prompt the user to validate.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ms365_consent') === 'success') {
      addNotification?.({
        type: 'info',
        title: 'Admin consent received',
        message: 'Microsoft sent you back. Click "Validate" to confirm the app can read mailboxes.'
      });
      // Clean the URL so the toast doesn't fire again on reload
      params.delete('ms365_consent');
      const newSearch = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
    }
  }, [addNotification]);

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;

  const consentUrl = adminConsentUrl();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Microsoft 365 / Outlook</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Register an Azure AD app once for your tenant; users in your organization can then sync their Outlook
            mailboxes through it. Application Permissions mode lets a service account read multiple mailboxes
            without each user authorizing.
          </p>
        </div>
        <span
          className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            config.validationStatus === 'valid'
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : config.validationStatus === 'invalid'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
          }`}
        >
          {config.validationStatus === 'valid' ? 'Connected'
            : config.validationStatus === 'invalid' ? 'Invalid'
            : config.validationStatus === 'pending' ? 'Pending'
            : 'Not configured'}
        </span>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={config.enabled}
          onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
          className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Enable Microsoft 365 integration</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Required for users to add Outlook accounts via your tenant's app registration.
          </span>
        </span>
      </label>

      {config.enabled && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Authentication Mode</label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="authType"
                  value="delegated"
                  checked={config.authType === 'delegated'}
                  onChange={() => setConfig(prev => ({ ...prev, authType: 'delegated' }))}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm">
                  <strong className="text-gray-900 dark:text-white">Delegated (per-user)</strong>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Each user signs in personally and grants WorkiDoc access to their own mailbox.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="authType"
                  value="application_permissions"
                  checked={config.authType === 'application_permissions'}
                  onChange={() => setConfig(prev => ({ ...prev, authType: 'application_permissions' }))}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm">
                  <strong className="text-gray-900 dark:text-white">Application Permissions (admin-consent, no per-user OAuth)</strong>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    The app reads any allowed mailbox in your tenant via client credentials. Requires admin consent
                    to <code>Mail.Read</code> at the application level.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Tenant ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={config.tenantId || ''}
                placeholder="2ae66a06-e784-4bdd-89ed-7a87198db9e5"
                onChange={(e) => setConfig(prev => ({ ...prev, tenantId: e.target.value.trim() || null }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Application (Client) ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={config.clientId || ''}
                placeholder="ddde3c62-696c-41ea-93f1-439a1c8ef968"
                onChange={(e) => setConfig(prev => ({ ...prev, clientId: e.target.value.trim() || null }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Client Secret {!config.hasClientSecret && <span className="text-red-500">*</span>}
            </label>
            <input
              type="password"
              autoComplete="new-password"
              value={clientSecretInput}
              placeholder={config.hasClientSecret ? '****configured**** — leave blank to keep' : 'Paste client secret value'}
              onChange={(e) => setClientSecretInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Stored encrypted (AES-256-GCM). The "Value" from Azure Portal → Certificates &amp; secrets, not the Secret ID.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Allowed Email Domains</label>
            <input
              type="text"
              value={allowedDomainsInput}
              placeholder="contoso.com, contoso.in"
              onChange={(e) => setAllowedDomainsInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Comma-separated. Only mailboxes on these domains can be synced under this app.
            </p>
          </div>

          {config.authType === 'application_permissions' && (
            <div className="rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">Admin consent required</h4>
              <p className="text-xs text-blue-800 dark:text-blue-200 mb-2">
                Application Permissions need a global / privileged role admin to grant tenant-wide consent to
                <code className="mx-1 px-1 bg-blue-100 dark:bg-blue-900/40 rounded">Mail.Read</code> (and any other
                Graph permissions you've added).
              </p>
              <div className="flex items-center gap-3 mt-2">
                {consentUrl ? (
                  <a
                    href={consentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline"
                  >
                    Grant admin consent →
                  </a>
                ) : (
                  <span className="text-xs text-blue-700 dark:text-blue-300">
                    Save Tenant ID + Client ID first, then come back here for the consent link.
                  </span>
                )}
                {config.adminConsentGranted && (
                  <span className="text-xs text-green-700 dark:text-green-300">✓ Consent recorded</span>
                )}
              </div>
            </div>
          )}

          {config.validationError && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-300">
              <strong>Last validation error:</strong> {config.validationError}
            </div>
          )}

          <ProvisionedMailboxesSection orgId={orgId} />
        </>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" onClick={handleValidate} disabled={validating || !config.enabled}>
          {validating ? <><LoadingSpinner size="sm" /> Validating...</> : 'Validate'}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><LoadingSpinner size="sm" /> Saving...</> : 'Save'}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// Provisioned mailboxes — admin assigns specific mailboxes to specific users
// ============================================================================

interface AssignmentRow {
  _id: string;
  email: string;
  displayName?: string;
  assignedTo: { _id: string; username?: string; email?: string; label?: string } | null;
  addedBy?: { _id: string; username?: string } | null;
  addedAt?: string;
  isActive?: boolean;
  // Projects this mailbox's mail may be classified into. Empty = all of the
  // assigned user's accessible projects.
  assignedProjects?: string[];
}

// Per-mailbox project picker. Only the assigned user's accessible projects are
// offered, all selected by default; deselect to narrow what this mailbox captures.
const MailboxProjectSelector: React.FC<{ orgId: string; assignment: AssignmentRow; onSaved: () => void }> = ({ orgId, assignment, onSaved }) => {
  const { addNotification } = useAppStore();
  const [expanded, setExpanded] = useState(false);
  const [projects, setProjects] = useState<Array<{ _id: string; projectName: string }>>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const userId = assignment.assignedTo?._id;
  const assigned = assignment.assignedProjects || [];

  const loadProjects = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res: any = await apiClient.orgSettings.getUserAccessibleProjects(orgId, userId);
      const list: Array<{ _id: string; projectName: string }> = res?.data ?? res ?? [];
      setProjects(Array.isArray(list) ? list : []);
      // Empty assignedProjects means "all" — default every project checked.
      setChecked(assigned.length ? new Set(assigned) : new Set(list.map(p => p._id)));
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Could not load projects', message: err?.response?.data?.error || err.message });
    } finally { setLoading(false); }
  }, [orgId, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && projects.length === 0) loadProjects();
  };
  const toggle = (id: string) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = projects.length > 0 && checked.size === projects.length;

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.orgSettings.updateMailboxProjects(orgId, assignment._id, Array.from(checked));
      addNotification?.({ type: 'success', title: 'Projects updated', message: `${assignment.email}: mail will only be captured for ${checked.size} selected project(s).` });
      onSaved();
      setExpanded(false);
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Update failed', message: err?.response?.data?.error || err.message });
    } finally { setSaving(false); }
  };

  const summary = assigned.length === 0 ? 'all projects' : `${assigned.length} project${assigned.length === 1 ? '' : 's'}`;

  return (
    <div className="mt-1">
      <button type="button" onClick={toggleExpand} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
        Projects: {summary} {expanded ? '▲' : '▼'}
      </button>
      {expanded && (
        <div className="mt-2 p-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          {loading ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">Loading projects…</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Only mail matching a checked project becomes a correspondence.</span>
                <button
                  type="button"
                  onClick={() => setChecked(allChecked ? new Set() : new Set(projects.map(p => p._id)))}
                  className="text-xs text-blue-600 dark:text-blue-400"
                >
                  {allChecked ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <div className="max-h-48 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
                {projects.map(p => (
                  <label key={p._id} className="flex items-center gap-2 text-xs text-gray-800 dark:text-gray-200">
                    <input type="checkbox" checked={checked.has(p._id)} onChange={() => toggle(p._id)} />
                    <span className="truncate">{p.projectName}</span>
                  </label>
                ))}
                {projects.length === 0 && <span className="text-xs text-gray-400 italic col-span-2">This user has no accessible projects.</span>}
              </div>
              <div className="mt-2 flex items-center gap-3">
                <Button onClick={save} disabled={saving} className="text-xs py-1 px-3">{saving ? 'Saving…' : 'Save projects'}</Button>
                <span className="text-xs text-gray-400">{checked.size} of {projects.length} selected</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ProvisionedMailboxesSection: React.FC<{ orgId: string }> = ({ orgId }) => {
  const { addNotification } = useAppStore();
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [orgUsers, setOrgUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [form, setForm] = useState({ email: '', displayName: '', assignedToUserId: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mailRes, userRes]: any[] = await Promise.all([
        apiClient.orgSettings.listProvisionedMailboxes(orgId),
        apiClient.users.list({ organization: orgId, limit: 500 } as any)
      ]);
      // Both endpoints get unwrapped by the api client (object response).
      const inner = mailRes?.data ?? mailRes ?? [];
      setRows(Array.isArray(inner) ? inner : []);
      // users.list returns either an array or {data, pagination} depending on backend.
      const list = (userRes as any)?.data || (userRes as any)?.users || (Array.isArray(userRes) ? userRes : []);
      setOrgUsers(list);
    } catch (err: any) {
      addNotification?.({
        type: 'error',
        title: 'Could not load provisioned mailboxes',
        message: err?.response?.data?.error || err.message
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, addNotification]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const email = form.email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      addNotification?.({ type: 'error', title: 'Invalid email', message: 'Enter a valid email address.' });
      return;
    }
    if (!form.assignedToUserId) {
      addNotification?.({ type: 'error', title: 'User required', message: 'Pick a user to assign the mailbox to.' });
      return;
    }
    setAdding(true);
    try {
      await apiClient.orgSettings.addProvisionedMailbox(orgId, {
        email,
        displayName: form.displayName.trim() || undefined,
        assignedToUserId: form.assignedToUserId
      });
      addNotification?.({ type: 'success', title: 'Mailbox assigned', message: `${email} provisioned successfully.` });
      setForm({ email: '', displayName: '', assignedToUserId: '' });
      await load();
    } catch (err: any) {
      const raw = err?.response?.data?.error ?? err?.message ?? 'Could not assign mailbox';
      addNotification?.({
        type: 'error',
        title: 'Assignment failed',
        message: typeof raw === 'string' ? raw : (raw?.message || JSON.stringify(raw))
      });
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (row: AssignmentRow) => {
    const who = row.assignedTo?.username || row.assignedTo?.email || 'this user';
    if (!confirm(`Remove ${row.email} from ${who}? If they were syncing, sync will stop immediately.`)) return;
    setRemovingId(row._id);
    try {
      await apiClient.orgSettings.removeProvisionedMailbox(orgId, row._id);
      addNotification?.({ type: 'success', title: 'Assignment removed', message: `${row.email} unassigned.` });
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Remove failed', message: err?.response?.data?.error || err.message });
    } finally {
      setRemovingId(null);
    }
  };

  // Group rows by assignedTo for display.
  const grouped = React.useMemo(() => {
    const map = new Map<string, { user: any; assignments: AssignmentRow[] }>();
    for (const r of rows) {
      const key = r.assignedTo?._id || '__unassigned__';
      if (!map.has(key)) {
        map.set(key, { user: r.assignedTo, assignments: [] });
      }
      map.get(key)!.assignments.push(r);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (!a.user) return 1;
      if (!b.user) return -1;
      return String(a.user.username || a.user.email || '').localeCompare(String(b.user.username || b.user.email || ''));
    });
  }, [rows]);

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-5 mt-2 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Provisioned mailboxes</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Assign specific Microsoft 365 mailboxes to specific users. Each user sees only the mailboxes you've assigned to them
          and can independently enable or disable sync on each one. The same mailbox can be assigned to multiple users (shared inboxes).
        </p>
      </div>

      {/* Add form */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 rounded-md bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
        <div className="md:col-span-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Assign to user *</label>
          <select
            value={form.assignedToUserId}
            onChange={(e) => setForm(prev => ({ ...prev, assignedToUserId: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="">— select —</option>
            {orgUsers.map((u: any) => (
              <option key={u._id || u.id} value={u._id || u.id}>
                {u.username || u.email}{u.role ? ` (${u.role})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="md:col-span-4">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mailbox email *</label>
          <input
            type="email"
            value={form.email}
            placeholder="user@yourcompany.com"
            onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Display name (optional)</label>
          <input
            type="text"
            value={form.displayName}
            onChange={(e) => setForm(prev => ({ ...prev, displayName: e.target.value }))}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
        <div className="md:col-span-1 flex items-end">
          <Button onClick={handleAdd} disabled={adding} className="w-full">
            {adding ? <LoadingSpinner size="sm" /> : 'Add'}
          </Button>
        </div>
      </div>

      {/* List grouped by user */}
      {loading ? (
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 italic">No mailboxes provisioned yet.</div>
      ) : (
        <div className="space-y-3">
          {grouped.map(({ user, assignments }) => (
            <div key={user?._id || 'unassigned'} className="rounded-md border border-gray-200 dark:border-gray-700">
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-900/60 text-xs font-medium text-gray-700 dark:text-gray-200">
                {user ? (user.username || user.email) : <span className="text-amber-600 dark:text-amber-400">Unassigned (admin attention needed)</span>}
                <span className="ml-2 text-gray-400">·</span>
                <span className="ml-2 text-gray-500 dark:text-gray-400">{assignments.length} mailbox{assignments.length === 1 ? '' : 'es'}</span>
              </div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {assignments.map(a => (
                  <li key={a._id} className="px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-gray-900 dark:text-white">{a.displayName ? `${a.displayName} <${a.email}>` : a.email}</span>
                        {a.isActive === false && <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">inactive</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemove(a)}
                        disabled={removingId === a._id}
                        className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 font-medium disabled:opacity-50"
                      >
                        {removingId === a._id ? 'Removing…' : 'Remove'}
                      </button>
                    </div>
                    <MailboxProjectSelector orgId={orgId} assignment={a} onSaved={load} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
